/**
 * A doubles rally on the hero court. Decorative only - no data, no counters.
 *
 * Two teams in their own kit colour, a ball crossing the net, paddles that
 * track it and footwork that moves with the point.
 *
 * Every athlete is written out rather than referenced with <use>: content
 * inside a <use> lives in a shadow tree that document CSS rules cannot reach,
 * which left the bodies unpainted while the paddles still rendered.
 */

interface AthleteProps {
  /** near court is drawn slightly larger and heavier than the far court */
  court: 'near' | 'far';
  /** class carrying the paddle-arm animation */
  arm: string;
}

function Athlete({ court, arm }: AthleteProps) {
  const near = court === 'near';

  return (
    <g className="ath">
      {/* legs, drawn first so the torso sits over the hips */}
      <path
        className="limb"
        d={near ? 'M0 -43 L-13 -25 L-18 -2' : 'M0 -42 L-11 -25 L-15 -2'}
        strokeWidth={near ? 9.6 : 9}
      />
      <path
        className="limb"
        d={near ? 'M3 -43 L15 -27 L19 -2' : 'M2 -42 L13 -26 L17 -2'}
        strokeWidth={near ? 9.6 : 9}
      />

      {/* shorts - the team colour */}
      <path
        className="kit"
        d={
          near
            ? 'M-7 -47 L9 -47 L11 -30 L3 -30 L1 -35 L-4 -30 L-9 -30 Z'
            : 'M-6 -46 L8 -46 L10 -30 L2 -30 L0 -34 L-4 -30 L-8 -30 Z'
        }
      />

      {/* torso, leaning into the court */}
      <path
        className="fig"
        d={
          near
            ? 'M-10 -70 Q-2 -75 6 -70 C9 -61 9 -53 8 -45 L-6 -45 C-11 -53 -12 -61 -10 -70 Z'
            : 'M-9 -68 Q-2 -73 5 -68 C8 -60 8 -52 7 -44 L-5 -44 C-10 -52 -11 -60 -9 -68 Z'
        }
      />
      <circle className="fig" cx={near ? -3 : -2} cy={near ? -79 : -77} r={near ? 8 : 7.6} />

      {/* free arm, held across the body for balance */}
      <path
        className="limb"
        d={near ? 'M5 -67 L13 -56 L11 -46' : 'M4 -65 L12 -55 L10 -45'}
        strokeWidth={near ? 6.8 : 6.4}
      />

      {/* paddle arm. The wrapper puts the shoulder at the local origin so the
          swing rotates around the shoulder joint. */}
      <g transform={near ? 'translate(-7 -66)' : 'translate(-6 -64)'}>
        <g className={`arm ${arm}`}>
          <path
            className="limb"
            d={near ? 'M0 0 L-12 -2 L-22 -6' : 'M0 0 L-11 -2 L-20 -6'}
            strokeWidth={near ? 7 : 6.6}
          />
          <g transform={`translate(${near ? '-22 -6' : '-20 -6'}) rotate(-18)`}>
            <rect
              className="pad"
              x={near ? -10 : -9}
              y={near ? -2.6 : -2.4}
              width={near ? 10 : 9}
              height={near ? 5.2 : 4.8}
              rx={near ? 2.6 : 2.4}
            />
            <ellipse
              className="pad"
              cx={near ? -20 : -18.5}
              cy={0}
              rx={near ? 8.8 : 8.2}
              ry={near ? 11.2 : 10.4}
            />
          </g>
        </g>
      </g>
    </g>
  );
}

interface PlayerProps {
  x: number;
  y: number;
  court: 'near' | 'far';
  /** right of the net or left of it - decides the kit colour */
  team: 'a' | 'b';
  /** players left of the net are mirrored so everyone faces the net */
  mirrored?: boolean;
  /** footwork class */
  move: string;
  /** paddle-arm class */
  arm: string;
}

function Player({ x, y, court, team, mirrored, move, arm }: PlayerProps) {
  const far = court === 'far';
  const figure = <Athlete court={court} arm={arm} />;

  return (
    <g
      className={`team-${team}${far ? ' back' : ''}`}
      transform={`translate(${x} ${y})${far ? ' scale(.72)' : ''}`}
    >
      <g className={`body ${move}`}>
        <ellipse
          className="shade"
          cx={mirrored ? -2 : 2}
          cy={0}
          rx={far ? 29 : 31}
          ry={far ? 7 : 7.5}
        />
        {mirrored ? <g transform="scale(-1 1)">{figure}</g> : figure}
      </g>
    </g>
  );
}

export default function Rally() {
  return (
    <svg
      className="rally"
      viewBox="0 0 1200 420"
      preserveAspectRatio="xMidYMax meet"
      aria-hidden="true"
      focusable="false"
    >
      {/* far court - covering, paddles up */}
      <Player x={700} y={258} court="far" team="a" move="sway-a" arm="guard-a" />
      <Player x={500} y={258} court="far" team="b" mirrored move="sway-b" arm="guard-b" />

      {/* near court - the two players trading the ball */}
      <Player x={800} y={362} court="near" team="a" move="hit-right" arm="swing-a" />
      <Player x={400} y={362} court="near" team="b" mirrored move="hit-left" arm="swing-b" />

      {/* the ball, its ground shadow, and two fading ghosts for speed */}
      <ellipse className="ball-shadow" rx="9" ry="3" />
      <circle className="ball trail-2" r="6" />
      <circle className="ball trail-1" r="6" />
      <circle className="ball" r="6.5" />
    </svg>
  );
}
